import React, { useState, useEffect } from 'react'
import TourTooltip from './TourTooltip'

export type TourType = 'admin' | 'tenant' | 'platform' | 'publish'

interface TourStep {
  title: string
  content: string
  targetSelector?: string
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center'
}

interface ProductTourProps {
  tourType: TourType
  onComplete: () => void
  onSkip: () => void
  autoStart?: boolean
  initialStep?: number
  onStepChange?: (stepIndex: number, tourType: TourType) => void
}

const ProductTour: React.FC<ProductTourProps> = ({
  tourType,
  onComplete,
  onSkip,
  autoStart = true,
  initialStep = 0,
  onStepChange
}) => {
  const [isActive, setIsActive] = useState(false)
  const [currentStep, setCurrentStep] = useState(initialStep)
  const [steps, setSteps] = useState<TourStep[]>([])
  const [isTransitioning, setIsTransitioning] = useState(false)

  useEffect(() => {
    // Load steps based on tour type
    setSteps(getTourSteps(tourType))
    // Set to initial step when tour type or initial step changes
    setCurrentStep(initialStep)

    if (autoStart) {
      // Small delay to ensure DOM is ready
      const timerId = setTimeout(() => {
        setIsActive(true)
      }, 500)

      return () => clearTimeout(timerId)
    }
  }, [tourType, autoStart, initialStep])

  // Notify parent of step changes for tab/sub-tab switching
  useEffect(() => {
    if (isActive && onStepChange) {
      // Set transitioning state to give React time to render new tab content
      setIsTransitioning(true)
      onStepChange(currentStep, tourType)

      // Wait for tab content to render before showing tooltip
      // Longer delay for nested tab switching (e.g., Knowledge Base -> CAIQ -> Configure)
      const timer = setTimeout(() => {
        setIsTransitioning(false)
      }, 600) // 600ms delay to allow nested React re-renders

      return () => clearTimeout(timer)
    }
  }, [currentStep, tourType, isActive, onStepChange])

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      handleComplete()
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSkip = () => {
    setIsActive(false)
    onSkip()
  }

  const handleComplete = () => {
    setIsActive(false)
    onComplete()
  }

  if (!isActive || steps.length === 0) {
    return null
  }

  const step = steps[currentStep]

  // Safety check: if step is undefined (edge case during transition), don't render
  if (!step) {
    return null
  }

  // Don't render tooltip while transitioning between tabs
  if (isTransitioning) {
    return null
  }

  const isLastStep = currentStep === steps.length - 1

  // For admin tour's last step, show "Next" since it continues to publish tour
  const nextButtonText = tourType === 'admin' && isLastStep ? 'Next' : undefined

  return (
    <TourTooltip
      targetSelector={step.targetSelector}
      title={step.title}
      content={step.content}
      currentStep={currentStep + 1}
      totalSteps={steps.length}
      position={step.position || 'bottom'}
      onNext={handleNext}
      onBack={handleBack}
      onSkip={handleSkip}
      onClose={handleSkip}
      showBack={currentStep > 0}
      isLastStep={isLastStep}
      nextButtonText={nextButtonText}
    />
  )
}

// Tour step definitions
const getTourSteps = (tourType: TourType): TourStep[] => {
  switch (tourType) {
    case 'admin':
      return [
        {
          title: 'Welcome to Your Trust Center! 🎉',
          content: `
            <p class="mb-3 text-lg">Let's take a quick tour to show you how to build and manage your trust center.</p>
            <p class="text-gray-600">This will take about 2 minutes. You can skip or retake this tour anytime from Settings.</p>
          `,
          position: 'center'
        },
        {
          title: 'Dashboard Overview',
          content: `
            <p class="mb-3">This is your main dashboard where you can see key metrics and quick stats about your trust center.</p>
            <p class="text-gray-600">Monitor access requests, document views, and overall engagement at a glance.</p>
          `,
          targetSelector: '[data-tour="dashboard-content"]',
          position: 'top'
        },
        {
          title: 'Navigation Tabs',
          content: `
            <p class="mb-2">These tabs organize your admin panel into sections:</p>
            <ul class="list-none space-y-2 mt-3">
              <li class="flex items-start gap-2">
                <span class="text-blue-600 font-bold">•</span>
                <span><strong>Dashboard</strong> - Overview and stats</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-blue-600 font-bold">•</span>
                <span><strong>Trust Center</strong> - Content and design</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-blue-600 font-bold">•</span>
                <span><strong>Requests</strong> - Access and privacy requests</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-blue-600 font-bold">•</span>
                <span><strong>Analytics</strong> - Detailed insights</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-blue-600 font-bold">•</span>
                <span><strong>Settings</strong> - Configuration options</span>
              </li>
            </ul>
          `,
          targetSelector: '[data-tour="navigation-tabs"]',
          position: 'bottom'
        },
        {
          title: 'Trust Center Content',
          content: `
            <p class="mb-3">Click the <strong>Trust Center</strong> tab to edit what visitors see on your public-facing trust center.</p>
            <p class="text-gray-600">You can manage documents, badges, content, and deployments from there.</p>
          `,
          targetSelector: '[data-tour-tab="trust-center"]',
          position: 'bottom'
        },
        {
          title: 'Access Requests',
          content: `
            <p class="mb-3">When visitors request access to gated documents, those requests appear in the <strong>Requests</strong> tab.</p>
            <p class="text-gray-600">You can approve or reject requests, and set up automatic allowlists for trusted domains.</p>
          `,
          targetSelector: '[data-tour-tab="requests"]',
          position: 'bottom'
        },
        {
          title: 'Knowledge Base',
          content: `
            <p class="mb-3">The <strong>Knowledge Base</strong> tab helps you manage security questionnaires and compliance documentation.</p>
            <p class="text-gray-600">Build a library of approved answers to common security questions for faster RFP responses.</p>
          `,
          targetSelector: '[data-tour-tab="knowledge-base"]',
          position: 'bottom'
        },
        {
          title: 'TPRM (Third-Party Risk)',
          content: `
            <p class="mb-3">The <strong>TPRM</strong> tab manages your vendor risk assessment program.</p>
            <p class="text-gray-600">Track vendors, manage assessments, and monitor third-party security posture.</p>
          `,
          targetSelector: '[data-tour-tab="tprm"]',
          position: 'bottom'
        },
        {
          title: 'Analytics & Insights',
          content: `
            <p class="mb-3">The <strong>Analytics</strong> tab provides detailed insights into trust center engagement.</p>
            <p class="text-gray-600">Track visitor behavior, document access patterns, and conversion metrics.</p>
          `,
          targetSelector: '[data-tour-tab="analytics"]',
          position: 'bottom'
        },
        {
          title: 'Settings & Configuration',
          content: `
            <p class="mb-3">The <strong>Settings</strong> tab contains:</p>
            <ul class="list-none space-y-2 mt-3">
              <li class="flex items-start gap-2">
                <span class="text-blue-600">✓</span>
                <span>Product management</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-blue-600">✓</span>
                <span>Custom domain setup</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-blue-600">✓</span>
                <span>Team & billing settings</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-blue-600">✓</span>
                <span>Integration options</span>
              </li>
            </ul>
          `,
          targetSelector: '[data-tour-tab="settings"]',
          position: 'bottom'
        },
        {
          title: "Ready to Publish? 🚀",
          content: `
            <p class="mb-4 text-lg font-semibold">You now know the basics of managing your trust center!</p>
            <div class="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-4 mb-4">
              <p class="text-lg font-bold text-blue-900 mb-2">🎯 Next Step: Publish Your First Trust Center</p>
              <p class="text-blue-800 text-sm mb-3">
                Would you like a guided tour on how to add content and publish your trust center for the first time?
              </p>
              <p class="text-blue-700 text-xs">
                This will take 2-3 minutes and show you exactly what to do.
              </p>
            </div>
            <p class="text-gray-600 text-sm">Click <strong>Next</strong> to continue to the publishing guide, or <strong>Skip</strong> to explore on your own.</p>
          `,
          position: 'center'
        }
      ]

    case 'tenant':
      return [
        {
          title: 'Welcome! 👋',
          content: `
            <p>This trust center provides transparency about our security, compliance, and privacy practices.</p>
          `,
          position: 'center'
        },
        {
          title: 'Navigation',
          content: `
            <p>Browse different sections to learn about certifications, documents, subprocessors, and system status.</p>
          `,
          position: 'center'
        },
        {
          title: 'Evidence Library',
          content: `
            <p>Access security documentation, audit reports, and compliance certifications.</p>
          `,
          position: 'center'
        },
        {
          title: "That's It!",
          content: `
            <p>Feel free to explore. If you need access to restricted documents, you can request it from the document page.</p>
          `,
          position: 'center'
        }
      ]

    case 'platform':
      return [
        {
          title: 'Platform Admin Tour',
          content: `
            <p>Manage multiple tenant trust centers from this central platform.</p>
          `,
          position: 'center'
        },
        {
          title: 'Tenant Management',
          content: `
            <p>View and manage all trust centers. Each tenant has isolated data and deployments.</p>
          `,
          position: 'center'
        },
        {
          title: 'Platform Control',
          content: `
            <p>You have full platform access. Use this responsibly to support tenant success.</p>
          `,
          position: 'center'
        }
      ]

    case 'publish':
      return [
        {
          title: 'Publishing Your First Trust Center! 📝',
          content: `
            <p class="mb-3 text-lg">Let's get your trust center live in just a few steps!</p>
            <p class="text-gray-600">We'll walk you through adding content, customizing branding, and deploying your site.</p>
          `,
          position: 'center'
        },
        {
          title: 'Trust Center Tab',
          content: `
            <p class="mb-3">This is where all the magic happens! The <strong>Trust Center</strong> tab is your content hub.</p>
            <p class="text-gray-600">You can manage documents, badges, page content, and deployments from here.</p>
          `,
          targetSelector: '[data-tour-tab="trust-center"]',
          position: 'bottom'
        },
        {
          title: 'Content Sub-Tab',
          content: `
            <p class="mb-3">Click on the <strong>Content</strong> sub-tab to edit your trust center's text and branding.</p>
            <p class="text-gray-600">This is where you'll set your company name, tagline, and page descriptions.</p>
          `,
          targetSelector: '[data-tour-subtab="content"]',
          position: 'bottom'
        },
        {
          title: 'Add Your Content',
          content: `
            <p class="mb-3">Fill in your:</p>
            <ul class="list-none space-y-2 mt-3">
              <li class="flex items-start gap-2">
                <span class="text-blue-600">1.</span>
                <span><strong>Company Name</strong> - Your organization's name</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-blue-600">2.</span>
                <span><strong>Tagline</strong> - A brief description</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-blue-600">3.</span>
                <span><strong>Overview</strong> - What makes your company trustworthy</span>
              </li>
            </ul>
          `,
          targetSelector: '[data-tour="content-area"]',
          position: 'top'
        },
        {
          title: 'Deployments',
          content: `
            <p class="mb-3">Once you've added content, go to the <strong>Deployments</strong> sub-tab.</p>
            <p class="text-gray-600">This is where you'll preview your changes and publish them live.</p>
          `,
          targetSelector: '[data-tour-subtab="deployments"]',
          position: 'bottom'
        },
        {
          title: 'Three-Version System',
          content: `
            <p class="mb-3">We use a safe deployment system:</p>
            <ul class="list-none space-y-2 mt-3">
              <li class="flex items-start gap-2">
                <span class="text-green-600">🟢</span>
                <span><strong>Live</strong> - What your visitors see (production)</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-blue-600">🔵</span>
                <span><strong>Preview</strong> - Test changes before publishing</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-gray-600">⚪</span>
                <span><strong>Last</strong> - Previous version (for rollback)</span>
              </li>
            </ul>
          `,
          targetSelector: '[data-tour="version-cards"]',
          position: 'top'
        },
        {
          title: 'Deploy to Preview',
          content: `
            <p class="mb-3">First, click <strong>"Deploy to Preview"</strong> to see your changes on a staging URL.</p>
            <p class="text-gray-600">This lets you test everything before going live. You'll get a preview.yoursite.com URL.</p>
          `,
          targetSelector: '[data-tour="deploy-preview-btn"]',
          position: 'left'
        },
        {
          title: 'Promote to Live',
          content: `
            <p class="mb-3">When you're happy with preview, click <strong>"Promote to Live"</strong>!</p>
            <p class="text-gray-600">This makes your trust center public on your main URL. Your previous live version becomes "Last" for safety.</p>
          `,
          targetSelector: '[data-tour="publish-live-btn"]',
          position: 'left'
        },
        {
          title: "Publishing Complete! Let's Continue 🎯",
          content: `
            <p class="mb-3">Great! You now know how to publish your trust center.</p>
            <p class="text-gray-600">Let's continue the tour to explore the other powerful features available to you.</p>
          `,
          position: 'center'
        },
        {
          title: 'Privacy Controls',
          content: `
            <p class="mb-3">Manage privacy settings and data collection preferences.</p>
            <p class="text-gray-600">Configure cookie consent banners, privacy policy links, and data handling preferences for your trust center visitors.</p>
          `,
          targetSelector: '[data-tour="privacy-portal"]',
          position: 'top'
        },
        {
          title: 'Requests Tab',
          content: `
            <p class="mb-3">Let's explore the <strong>Requests</strong> tab where you manage access to your trust center.</p>
            <p class="text-gray-600">This is your command center for controlling who can access gated documents.</p>
          `,
          targetSelector: '[data-tour-tab="requests"]',
          position: 'bottom'
        },
        {
          title: 'Access Requests List',
          content: `
            <p class="mb-3">This is where all access requests from visitors appear.</p>
            <p class="text-gray-600">You can search, filter by status (pending/approved/rejected), and take action on each request. Click approve or reject based on your criteria.</p>
          `,
          targetSelector: '[data-tour="requests-list"]',
          position: 'top'
        },
        {
          title: 'Privacy Requests',
          content: `
            <p class="mb-3">Manage GDPR, CCPA, and other privacy-related requests.</p>
            <p class="text-gray-600">Handle data access requests, deletion requests, and opt-out preferences. Track status and maintain compliance with privacy regulations.</p>
          `,
          targetSelector: '[data-tour="privacy-requests"]',
          position: 'top'
        },
        {
          title: 'Knowledge Base',
          content: `
            <p class="mb-3">Now let's explore the <strong>Knowledge Base</strong> for security questionnaire management.</p>
            <p class="text-gray-600">This powerful system helps you answer security questionnaires using AI.</p>
          `,
          targetSelector: '[data-tour-tab="knowledge-base"]',
          position: 'bottom'
        },
        {
          title: 'Query Knowledge Base',
          content: `
            <p class="mb-3">Ask questions about your security practices here.</p>
            <p class="text-gray-600">AI searches your knowledge base and documents to provide accurate answers. Perfect for quick lookups during sales calls or security reviews.</p>
          `,
          targetSelector: '[data-tour="kb-query"]',
          position: 'top'
        },
        {
          title: 'Questionnaire Upload',
          content: `
            <p class="mb-3">Upload CSV or Excel files with vendor security questions.</p>
            <p class="text-gray-600">The system uses AI to match questions against your knowledge base and generate responses. Speeds up RFPs and security questionnaires dramatically.</p>
          `,
          targetSelector: '[data-tour="kb-questionnaire-upload"]',
          position: 'top'
        },
        {
          title: 'CAIQ Configuration',
          content: `
            <p class="mb-3">Configure AI-powered CAIQ (Consensus Assessments) scanning.</p>
            <p class="text-gray-600">Point the system at your documentation sources and it automatically extracts answers to 286 CAIQ v4 questions. Build your security questionnaire knowledge base automatically.</p>
          `,
          targetSelector: '[data-tour="kb-caiq-configure"]',
          position: 'top'
        },
        {
          title: 'CAIQ Scan & Extract',
          content: `
            <p class="mb-3">Run AI-powered scans to extract CAIQ answers from your documents.</p>
            <p class="text-gray-600">The system processes your uploaded security documents and web sources to automatically generate answers to all 286 CAIQ questions.</p>
          `,
          targetSelector: '[data-tour="kb-caiq-scan"]',
          position: 'top'
        },
        {
          title: 'Review Extracted Answers',
          content: `
            <p class="mb-3">Review, edit, and approve AI-generated CAIQ answers.</p>
            <p class="text-gray-600">See confidence scores, filter by status, resolve conflicts, and save approved answers to your knowledge base.</p>
          `,
          targetSelector: '[data-tour="kb-caiq-review"]',
          position: 'top'
        },
        {
          title: 'Browse CAIQ Questions',
          content: `
            <p class="mb-3">Explore all 286 CAIQ v4 questions organized by domain.</p>
            <p class="text-gray-600">Search across domains like Application Security, Data Governance, and Infrastructure Security. View saved answers and mappings.</p>
          `,
          targetSelector: '[data-tour="kb-caiq-browse"]',
          position: 'top'
        },
        {
          title: 'Knowledge Base Analytics',
          content: `
            <p class="mb-3">Track completion progress and answer quality metrics.</p>
            <p class="text-gray-600">View completion rates by domain, confidence distribution, and activity logs to monitor your knowledge base growth.</p>
          `,
          targetSelector: '[data-tour="kb-caiq-analytics"]',
          position: 'top'
        },
        {
          title: 'TPRM (Third-Party Risk)',
          content: `
            <p class="mb-3">The <strong>TPRM</strong> tab manages your third-party risk program.</p>
            <p class="text-gray-600">TPRM has two main areas: Vendor Risk Assessment for security reviews, and Contract Management for tracking vendor contracts.</p>
          `,
          targetSelector: '[data-tour-tab="tprm"]',
          position: 'bottom'
        },
        {
          title: 'Vendor Risk Assessment',
          content: `
            <p class="mb-3">The <strong>Vendor Risk Assessment</strong> sub-tab helps you evaluate vendor security.</p>
            <p class="text-gray-600">AI analyzes vendor websites for certifications (SOC 2, ISO 27001), privacy policies, and security posture. Get automated risk scores and approval recommendations.</p>
          `,
          targetSelector: '[data-tour-subtab="tprm-vendors"]',
          position: 'bottom'
        },
        {
          title: 'TPRM Dashboard',
          content: `
            <p class="mb-3">The dashboard provides an overview of your vendor risk landscape.</p>
            <p class="text-gray-600">View risk distribution, approval statuses, and key metrics across all vendors. Track auto-approved vs. requiring-review assessments at a glance.</p>
          `,
          targetSelector: '[data-tour="tprm-dashboard"]',
          position: 'top'
        },
        {
          title: 'Vendor Assessments',
          content: `
            <p class="mb-3">This is where you submit and review vendor security assessments.</p>
            <p class="text-gray-600">AI analyzes vendor websites for security posture (SOC 2, ISO 27001, privacy policies). Get data classification recommendations and approval workflows automatically.</p>
          `,
          targetSelector: '[data-tour="tprm-assessments"]',
          position: 'top'
        },
        {
          title: 'TPRM Configuration',
          content: `
            <p class="mb-3">Configure your vendor risk assessment policies and data classification rules.</p>
            <p class="text-gray-600">Define security attestation requirements, approval thresholds, and custom risk criteria for different data classification levels.</p>
          `,
          targetSelector: '[data-tour="tprm-settings"]',
          position: 'top'
        },
        {
          title: 'Contract Management',
          content: `
            <p class="mb-3">TPRM also includes powerful <strong>Contract Management</strong> features.</p>
            <p class="text-gray-600">Upload vendor contracts, extract key metadata with AI, track renewals, and detect duplicates automatically.</p>
          `,
          targetSelector: '[data-tour="contracts-tabs"]',
          position: 'top'
        },
        {
          title: 'Contracts Dashboard',
          content: `
            <p class="mb-3">View and manage all vendor contracts in one place.</p>
            <p class="text-gray-600">AI automatically extracts vendor names, contract values, dates, and document types. Track upcoming renewals and filter by status.</p>
          `,
          targetSelector: '[data-tour="contracts-dashboard"]',
          position: 'top'
        },
        {
          title: 'Duplicate Detection',
          content: `
            <p class="mb-3">The <strong>Duplicates</strong> tab helps identify duplicate contracts.</p>
            <p class="text-gray-600">AI matches contracts by vendor name, value (within 1%), and dates. Keep your contract database clean and accurate.</p>
          `,
          targetSelector: '[data-tour-subtab="contracts-duplicates"]',
          position: 'bottom'
        },
        {
          title: 'Customer Contracts',
          content: `
            <p class="mb-3">The <strong>Customers</strong> tab tracks your customer contracts and ARR.</p>
            <p class="text-gray-600">Monitor total ARR, active customers, churn rates, and upcoming renewals. Get alerts for contracts expiring soon.</p>
          `,
          targetSelector: '[data-tour-subtab="contracts-customers"]',
          position: 'bottom'
        },
        {
          title: 'Analytics & Insights',
          content: `
            <p class="mb-3">Now let's check out the <strong>Analytics</strong> tab for powerful insights.</p>
            <p class="text-gray-600">Understand how visitors engage with your trust center and measure its effectiveness.</p>
          `,
          targetSelector: '[data-tour-tab="analytics"]',
          position: 'bottom'
        },
        {
          title: 'Analytics Dashboard',
          content: `
            <p class="mb-3">This overview dashboard shows key metrics at a glance.</p>
            <p class="text-gray-600">Track total requests, pending items, approval rates, and document views. Monitor trends over time to optimize your trust center performance.</p>
          `,
          targetSelector: '[data-tour="analytics-overview"]',
          position: 'top'
        },
        {
          title: 'Engagement Scoring',
          content: `
            <p class="mb-3">Measure how prospects engage with your trust center.</p>
            <p class="text-gray-600">Track engagement scores, identify hot leads, and see which documents resonate most with visitors. Understand visitor behavior patterns.</p>
          `,
          targetSelector: '[data-tour="analytics-engagement"]',
          position: 'top'
        },
        {
          title: 'Audit Trail',
          content: `
            <p class="mb-3">Complete audit log of all trust center activities.</p>
            <p class="text-gray-600">Track document access, approval decisions, configuration changes, and user actions. Filter by date, user, or event type for compliance.</p>
          `,
          targetSelector: '[data-tour="analytics-audit"]',
          position: 'top'
        },
        {
          title: 'Revenue Attribution',
          content: `
            <p class="mb-3">Connect trust center engagement to revenue outcomes.</p>
            <p class="text-gray-600">Link visitors to deals, track conversion rates, and measure the ROI of your trust center. Integrate with your CRM for sales insights.</p>
          `,
          targetSelector: '[data-tour="analytics-revenue"]',
          position: 'top'
        },
        {
          title: 'Settings & Configuration',
          content: `
            <p class="mb-3">Finally, let's look at the <strong>Settings</strong> tab for system configuration.</p>
            <p class="text-gray-600">This is your control center for all administrative settings.</p>
          `,
          targetSelector: '[data-tour-tab="settings"]',
          position: 'bottom'
        },
        {
          title: 'Product Management',
          content: `
            <p class="mb-3">Manage your product catalog here.</p>
            <p class="text-gray-600">Define your products/services that appear in your trust center. Visitors can filter documents and information by product.</p>
          `,
          targetSelector: '[data-tour="settings-products"]',
          position: 'top'
        },
        {
          title: 'Custom Domain Setup',
          content: `
            <p class="mb-3">Configure your custom domain for the trust center.</p>
            <p class="text-gray-600">Host your trust center on your own domain (e.g., trust.yourcompany.com) instead of the default subdomain. Includes SSL certificate setup and DNS configuration.</p>
          `,
          targetSelector: '[data-tour="settings-domain"]',
          position: 'top'
        },
        {
          title: 'Billing & Subscription',
          content: `
            <p class="mb-3">Manage your subscription plan and billing information.</p>
            <p class="text-gray-600">View current plan, update payment methods, review invoices, and manage subscription settings. Upgrade or downgrade your plan as needed.</p>
          `,
          targetSelector: '[data-tour="settings-billing"]',
          position: 'top'
        },
        {
          title: 'Team Management',
          content: `
            <p class="mb-3">Manage team members and their access permissions.</p>
            <p class="text-gray-600">Invite users, assign roles (admin/editor/reviewer/viewer), manage allowlists, and control who can access your trust center admin panel.</p>
          `,
          targetSelector: '[data-tour="settings-team"]',
          position: 'top'
        },
        {
          title: 'Integrations & API',
          content: `
            <p class="mb-3">Connect external tools and manage API access.</p>
            <p class="text-gray-600">Configure API keys for AI features (Anthropic, OpenAI), set up webhooks, and integrate with CRM systems for revenue attribution.</p>
          `,
          targetSelector: '[data-tour="settings-integrations"]',
          position: 'top'
        },
        {
          title: 'Account Security',
          content: `
            <p class="mb-3">Protect your account with advanced security features.</p>
            <p class="text-gray-600">Enable passkeys for passwordless authentication, manage security settings, and configure two-factor authentication options.</p>
          `,
          targetSelector: '[data-tour="settings-security"]',
          position: 'top'
        },
        {
          title: "Tour Complete! 🎉",
          content: `
            <p class="mb-4 text-lg font-semibold">You've completed the full tour!</p>
            <div class="bg-gradient-to-br from-green-50 to-blue-50 border-2 border-green-300 rounded-xl p-4 mb-4">
              <p class="text-lg font-bold text-green-900 mb-2">✅ You're all set to:</p>
              <ul class="text-sm text-green-800 space-y-2 mt-3">
                <li class="flex items-start gap-2">
                  <span class="text-green-600">✓</span>
                  <span>Add content and publish your trust center</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="text-green-600">✓</span>
                  <span>Manage access requests and allowlists</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="text-green-600">✓</span>
                  <span>Build your security knowledge base</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="text-green-600">✓</span>
                  <span>Track vendors with TPRM</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="text-green-600">✓</span>
                  <span>Monitor analytics and engagement</span>
                </li>
              </ul>
            </div>
            <p class="text-gray-600 text-sm">You can retake this tour anytime from Settings > General.</p>
          `,
          position: 'center'
        }
      ]

    default:
      return []
  }
}

export default ProductTour
